import React from 'react';
import type { WorksheetAuditEntry, WorksheetAuditLinkKind } from '../../services/programWorksheetAuditService';
import {
  worksheetRefLinkStateLabel,
  type WorksheetRefLinkState,
} from '../../utils/programWorksheetLinkState';

function linkKindClass(kind: WorksheetAuditLinkKind): string {
  if (kind === 'orphan_unreferenced_ref') {
    return 'cp-presentation-audit-row--orphan';
  }
  return '';
}

function linkStateClass(state: WorksheetRefLinkState | null | undefined): string {
  if (state === 'ref_no_document' || state === 'document_no_ref') {
    return 'cp-presentation-audit-row--link-warning';
  }
  return '';
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type WorksheetAuditSectionProps = {
  entries: WorksheetAuditEntry[];
  selected: Set<string>;
  onToggle: (key: string) => void;
  onSelectSectionKeys: (keys: string[], checked: boolean) => void;
  emptyMessage?: string;
};

const WorksheetAuditSection: React.FC<WorksheetAuditSectionProps> = ({
  entries,
  selected,
  onToggle,
  onSelectSectionKeys,
  emptyMessage = '활동지 데이터가 없습니다.',
}) => {
  const keys = entries.map((e) => e.selectionKey);
  const orphanCount = entries.filter((e) => e.linkKind === 'orphan_unreferenced_ref').length;
  const refAndDocumentCount = entries.filter((e) => e.linkState === 'ref_and_document').length;
  const refNoDocumentCount = entries.filter((e) => e.linkState === 'ref_no_document').length;
  const documentNoRefCount = entries.filter((e) => e.linkState === 'document_no_ref').length;

  return (
    <section className="cp-program-data-cleanup-section">
      <div className="cp-program-data-cleanup-section-head">
        <h4>활동지 — program_content_worksheets ({entries.length}건)</h4>
        {keys.length ? (
          <button
            type="button"
            className="cp-btn-secondary cp-program-data-cleanup-select-all"
            onClick={() => onSelectSectionKeys(keys, keys.some((k) => !selected.has(k)))}
          >
            {keys.every((k) => selected.has(k)) ? '전체 해제' : '전체 선택'}
          </button>
        ) : null}
      </div>
      <p className="page-desc cp-program-data-cleanup-section-desc">
        학습내용 <code>worksheet_ref_id</code> 컬럼과 DB 활동지 문서(<code>program_content_worksheets</code>) 연결
        상태입니다. <strong>ref + 활동지</strong> {refAndDocumentCount}건 · <strong>ref만</strong> {refNoDocumentCount}
        건 · <strong>활동지만</strong> {documentNoRefCount}건
        {orphanCount > 0 ? (
          <>
            {' '}
            · <span className="cp-presentation-audit-orphan-note">고아 {orphanCount}건</span>
          </>
        ) : null}
        . 활동지 삭제는 문서만 지우며, <code>empty-worksheet-ref:</code> 항목은 ref만 제거합니다.
      </p>
      {entries.length ? (
        <div className="cp-program-data-cleanup-table-wrap">
          <table className="cp-program-data-cleanup-table cp-program-data-cleanup-table--db cp-presentation-audit-table">
            <thead>
              <tr>
                <th className="cp-program-data-cleanup-col-check" scope="col" aria-label="선택" />
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>worksheet_ref_id</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>pages</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>document_bytes</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity">
                  <code>context</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>learning_text</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>link_state</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>link_status</code>
                </th>
                <th scope="col" className="cp-program-data-cleanup-col-db">
                  <code>updated_at</code>
                </th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry) => (
                <tr
                  key={entry.selectionKey}
                  className={[linkKindClass(entry.linkKind), linkStateClass(entry.linkState)].filter(Boolean).join(' ')}
                >
                  <td className="cp-program-data-cleanup-col-check">
                    <input
                      type="checkbox"
                      checked={selected.has(entry.selectionKey)}
                      onChange={() => onToggle(entry.selectionKey)}
                      aria-label={`${entry.linkLabel} ${entry.worksheetRefId}`}
                    />
                  </td>
                  <td className="cp-program-data-cleanup-col-db" title={entry.worksheetRefId}>
                    {entry.worksheetRefId.slice(0, 8)}…
                  </td>
                  <td className="cp-program-data-cleanup-col-db">{entry.pageCount}</td>
                  <td className="cp-program-data-cleanup-col-db">{formatBytes(entry.documentBytes)}</td>
                  <td className="cp-program-data-cleanup-col-db cp-program-data-cleanup-col-identity" title={entry.contextLabel}>
                    {entry.contextLabel}
                  </td>
                  <td className="cp-program-data-cleanup-col-db" title={entry.learningSnippet}>
                    {entry.learningSnippet}
                  </td>
                  <td className="cp-program-data-cleanup-col-db" title={worksheetRefLinkStateLabel(entry.linkState)}>
                    {worksheetRefLinkStateLabel(entry.linkState)}
                  </td>
                  <td className="cp-program-data-cleanup-col-db" title={entry.linkLabel}>
                    {entry.linkLabel}
                  </td>
                  <td className="cp-program-data-cleanup-col-db">{entry.lastUpdatedAt?.slice(0, 19) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="page-desc">{emptyMessage}</p>
      )}
    </section>
  );
};

export default WorksheetAuditSection;
